// crewbit-admin/src/components/money/proposals/ProposalReasoningTooltip.tsx
//
// Tier 2-UI Phase B Phase B (admin) — companion to ProposalPill. Mirrors
// crewbit-mobile/components/money/proposals/ProposalReasoningTooltip.tsx body
// decisions per `feedback_admin_pair_must_match_mobile_body`. Web idiom:
// position:fixed scrim + click-outside-to-dismiss + Esc keyboard handler
// (NOT mobile's Modal pageSheet — admin doesn't import RN). Reuses the
// MarkPaidModal / ConfirmDialog / OverridePillTooltip overlay pattern.
//
// Carries human-readable reasoning text derived from
// ProposedCategorization.reasoning so operators understand why a category /
// CCA class / deductibility was chosen.

import { useEffect } from 'react';
import { palette, radii, shadow, spacing, typography } from '../../../styles/tokens';

interface Props {
  visible: boolean;
  title?: string;
  reasoning: string;
  onDismiss: () => void;
}

export default function ProposalReasoningTooltip({ visible, title, reasoning, onDismiss }: Props) {
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [visible, onDismiss]);

  if (!visible) return null;

  return (
    <div
      onClick={onDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.white,
          borderRadius: radii.md,
          padding: spacing.lg,
          maxWidth: 420,
          width: '90%',
          boxShadow: shadow.lg,
          display: 'flex',
          flexDirection: 'column',
          gap: spacing.sm,
        }}
      >
        <div
          style={{
            fontSize: typography.size.heading,
            fontWeight: typography.weight.semibold,
            color: palette.g800,
          }}
        >
          {title ?? 'How was this determined?'}
        </div>
        <div
          style={{
            fontSize: typography.size.body,
            color: palette.g700,
            lineHeight: typography.lineHeight.normal,
          }}
        >
          {reasoning}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          style={{
            alignSelf: 'flex-end',
            background: palette.accent,
            color: palette.white,
            border: 'none',
            borderRadius: radii.sm,
            paddingInline: spacing.md,
            paddingBlock: spacing.sm,
            fontSize: typography.size.body,
            fontWeight: typography.weight.semibold,
            fontFamily: typography.fontFamily,
            cursor: 'pointer',
            marginTop: spacing.xs,
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
